/**
 * Card hover preview component
 * Wraps any element and shows a larger card image preview on hover.
 * Uses React Portal for proper z-index layering and viewport-aware positioning.
 * Shows both faces side-by-side for dual-faced cards when space permits.
 */
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { deriveScryfallImageUrl, hasDualFaceImages } from '@/lib/imageUrl';
import type { CardFace, CardLayout, ImageUris } from '@/types/card';

export interface CardHoverPreviewProps {
  /** Image URIs for the card preview */
  imageUris: Partial<ImageUris> | null;
  /** Card name for alt text */
  name: string;
  /** Content to wrap with hover behavior */
  children: ReactNode;
  /** Card layout — needed for DFC side-by-side detection */
  layout?: CardLayout | null;
  /** Card faces — needed for DFC side-by-side images */
  cardFaces?: CardFace[] | null;
  /** Delay before showing preview (ms) */
  delay?: number;
  /** Whether the preview is disabled */
  disabled?: boolean;
}

/** Scryfall image display dimensions */
const NORMAL_WIDTH = 488;
const NORMAL_HEIGHT = 680;
const LARGE_WIDTH = 672;
const LARGE_HEIGHT = 936;
/** Minimum preview dimensions */
const MIN_WIDTH = 244;
const MIN_HEIGHT = 340;
/** Card aspect ratio */
const ASPECT_RATIO = 5 / 7;

const VIEWPORT_PADDING = 16;
const CURSOR_OFFSET = 16;
const DFC_GAP = 8;

/** Use large display size on HiDPI displays (devicePixelRatio >= 2) */
function getTargetDimensions(): { width: number; height: number } {
  if (window.devicePixelRatio >= 2) {
    return { width: LARGE_WIDTH, height: LARGE_HEIGHT };
  }
  return { width: NORMAL_WIDTH, height: NORMAL_HEIGHT };
}

export interface PreviewLayout {
  x: number;
  y: number;
  cardWidth: number;
  cardHeight: number;
  showBothFaces: boolean;
}

/** Calculate preview size and position, fitting to viewport */
export function calculatePreviewLayout(mouseX: number, mouseY: number, isDualFace: boolean): PreviewLayout {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const availableWidth = vw - VIEWPORT_PADDING * 2 - CURSOR_OFFSET;
  const availableHeight = vh - VIEWPORT_PADDING * 2;

  // Use normal (488×680) by default; large (672×936) on HiDPI displays
  const target = getTargetDimensions();
  let cardWidth = target.width;
  let cardHeight = target.height;

  // Scale down to fit available height
  if (cardHeight > availableHeight) {
    cardHeight = availableHeight;
    cardWidth = Math.round(cardHeight * ASPECT_RATIO);
  }

  // Determine if DFC side-by-side fits
  let showBothFaces = false;
  if (isDualFace) {
    const dualWidth = cardWidth * 2 + DFC_GAP;
    if (dualWidth <= availableWidth) {
      showBothFaces = true;
    } else {
      // Try scaling down to fit dual
      const maxCardWidthForDual = Math.floor((availableWidth - DFC_GAP) / 2);
      const scaledHeight = Math.round(maxCardWidthForDual / ASPECT_RATIO);
      if (maxCardWidthForDual >= MIN_WIDTH && scaledHeight >= MIN_HEIGHT) {
        cardWidth = maxCardWidthForDual;
        cardHeight = scaledHeight;
        showBothFaces = true;
      }
      // else: can't fit dual even at min size, show single front face
    }
  }

  // Ensure single card fits available width
  if (cardWidth > availableWidth) {
    cardWidth = availableWidth;
    cardHeight = Math.round(cardWidth / ASPECT_RATIO);
  }

  // Enforce minimum
  if (cardWidth < MIN_WIDTH) {
    cardWidth = MIN_WIDTH;
    cardHeight = MIN_HEIGHT;
  }

  const totalWidth = showBothFaces ? cardWidth * 2 + DFC_GAP : cardWidth;

  // Position: prefer right of cursor, then left; prefer below cursor, then adjust
  let x = mouseX + CURSOR_OFFSET;
  let y = mouseY + CURSOR_OFFSET;

  if (x + totalWidth + VIEWPORT_PADDING > vw) {
    x = mouseX - totalWidth - CURSOR_OFFSET;
  }
  if (y + cardHeight + VIEWPORT_PADDING > vh) {
    y = vh - cardHeight - VIEWPORT_PADDING;
  }
  if (y < VIEWPORT_PADDING) {
    y = VIEWPORT_PADDING;
  }
  if (x < VIEWPORT_PADDING) {
    x = VIEWPORT_PADDING;
  }

  return { x, y, cardWidth, cardHeight, showBothFaces };
}

/** Get the large source URL from image URIs, deriving from available URLs if needed */
function getLargeUrl(imageUris: Partial<ImageUris> | null): string | null {
  if (!imageUris) return null;
  if (imageUris.large) return imageUris.large;
  if (imageUris.normal) return deriveScryfallImageUrl(imageUris.normal, 'large');
  if (imageUris.small) return deriveScryfallImageUrl(imageUris.small, 'large');
  return null;
}

/** Portal-based preview rendering — usable standalone for imperative patterns */
export interface CardHoverPreviewPortalProps {
  imageUris: Partial<ImageUris> | null;
  name: string;
  layout?: PreviewLayout;
  cardLayout?: CardLayout | null;
  cardFaces?: CardFace[] | null;
}

export function CardHoverPreviewPortal({
  imageUris,
  name,
  layout,
  cardLayout,
  cardFaces,
}: CardHoverPreviewPortalProps): ReactElement | null {
  if (!layout || !imageUris) return null;

  const isDualFace = hasDualFaceImages(cardLayout, cardFaces);
  const showBothFaces = layout.showBothFaces && isDualFace;

  const frontUris = showBothFaces ? cardFaces![0].image_uris : imageUris;
  const frontUrl = getLargeUrl(frontUris);
  const backUrl = showBothFaces ? getLargeUrl(cardFaces![1].image_uris) : null;

  if (!frontUrl) return null;

  return createPortal(
    <div className="pointer-events-none fixed z-9999" style={{ left: layout.x, top: layout.y }}>
      <div className="animate-in fade-in zoom-in-95 flex gap-2 duration-150">
        <div className="border-storm bg-obsidian overflow-hidden rounded-xl border shadow-2xl">
          <img
            src={frontUrl}
            alt={showBothFaces ? cardFaces![0].name : name}
            width={layout.cardWidth}
            height={layout.cardHeight}
            className="block"
          />
        </div>
        {showBothFaces && backUrl && (
          <div className="border-storm bg-obsidian overflow-hidden rounded-xl border shadow-2xl">
            <img src={backUrl} alt={cardFaces![1].name} width={layout.cardWidth} height={layout.cardHeight} className="block" />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function CardHoverPreview({
  imageUris,
  name,
  children,
  layout: cardLayout,
  cardFaces,
  delay = 250,
  disabled = false,
}: CardHoverPreviewProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [previewLayout, setPreviewLayout] = useState<PreviewLayout | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isDualFace = hasDualFaceImages(cardLayout, cardFaces);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !imageUris) return;

      const layout = calculatePreviewLayout(e.clientX, e.clientY, isDualFace);
      setPreviewLayout(layout);

      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    },
    [disabled, imageUris, delay, isDualFace],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isVisible && !timeoutRef.current) return;

      const layout = calculatePreviewLayout(e.clientX, e.clientY, isDualFace);
      setPreviewLayout(layout);
    },
    [isVisible, isDualFace],
  );

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Hide on scroll
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      setIsVisible(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isVisible]);

  return (
    <>
      <div
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>

      {isVisible && (
        <CardHoverPreviewPortal
          imageUris={imageUris}
          name={name}
          layout={previewLayout ?? undefined}
          cardLayout={cardLayout}
          cardFaces={cardFaces}
        />
      )}
    </>
  );
}
